import {
  ARTIFACTS_PATH,
  AssetTypeSchema,
  DeviceSlotSchema,
  type CandidateResource,
  type ExperimentResource,
  type StoreListingResource,
  type Target,
  type UpdateExperimentRequest,
} from '@studio/shared';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { Failure } from '../../shell/EmptyState.js';
import { useAsync, type Loaded } from '../../shell/useAsync.js';
import { Link, useNavigate, type Surface } from '../../shell/router.js';
import { useResourceEvents } from '../../shell/stream.js';
import { assetTypeLabel, deviceSlotLabel } from '../../app/history.js';
import { materialName, retrievalNote } from '../../app/material.js';
import { useExperiment } from '../useExperiment.js';
import { BriefSuggestions } from '../learn/Shelf.js';
import { countryName, languageName } from '../../inventory/model.js';
import { useSpreadSize } from '../../shell/spread-size.js';

/**
 * The Brief. One free field, one required choice, and nothing standing between the
 * operator and the first spread. Everything written here is saved as it is left - and
 * what has not been left yet goes with the press that reads it - so `Generate` is never
 * a form submission and never asks to be saved first.
 */
function Brief({ experimentId }: { experimentId: string }): ReactNode {
  const open = useExperiment(experimentId);
  const navigate = useNavigate();
  const spreadSize = useSpreadSize();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const experiment = open.detail?.experiment ?? null;
  const appId = experiment?.appId ?? null;
  /**
   * The Inventory this Brief was started from, when it was started from one.
   *
   * The way back is in the address, and it carries the search, the filter and the open
   * cell with it: a return has to land on the same reading, not on the whole list again.
   */
  const fromInventory = new URLSearchParams(window.location.search).get('back');
  /**
   * Read here rather than by the field: the first spread fixes the Store Listing, so it
   * waits until the choice is on the screen instead of going out on Main by default.
   */
  const listings = useAsync(async () => (appId === null ? null : api.readStoreListings(appId)), [appId]);

  /**
   * Edits leave in the order they were made, and a command can wait for all of them.
   *
   * `Generate` is not a form submission, but it does read what the operator has just
   * written, so it may not overtake it: `POST /spread` sent in the same tick as the
   * `PATCH` that carries the Brief reaches a server that has not been told the Brief
   * yet, and on an App with nothing else to explore from that is a refusal for text
   * the operator can see in the field.
   */
  const settled = useRef<Promise<void>>(Promise.resolve());
  const patch = useCallback(
    (change: UpdateExperimentRequest): Promise<boolean> => {
      setError(null);
      const done = settled.current.then(async (): Promise<boolean> => {
        try {
          await api.updateExperiment(experimentId, change);
          open.refresh();
          return true;
        } catch (caught) {
          setError(caught instanceof ApiError ? caught.message : 'Это изменение не удалось сохранить.');
          return false;
        }
      });
      settled.current = done.then(() => undefined);
      return done;
    },
    [experimentId, open],
  );

  /**
   * The Brief as it stands on the screen. `null` means the operator has not touched it
   * in this visit, so the Experiment's own copy is what shows; once they have typed,
   * their words own the field and no later reply overwrites them.
   */
  const [typed, setTyped] = useState<string | null>(null);
  const briefText = typed ?? experiment?.brief ?? '';
  const savedBrief = experiment?.brief ?? '';

  /**
   * The one place the Brief is sent, whether the edit ended in a blur or in a press.
   *
   * Whichever of the two comes first sends the text; the other finds the same text
   * already on its way and waits for that send rather than making a second one.
   */
  const sending = useRef<{ text: string; done: Promise<boolean> } | null>(null);
  const saveBrief = useCallback((): Promise<boolean> => {
    const text = typed;
    if (text === null || text === savedBrief) return Promise.resolve(true);
    const already = sending.current;
    if (already !== null && already.text === text) return already.done;
    const done = patch({ brief: text });
    sending.current = { text, done };
    // A send that failed is not a send that is on its way: the next press starts a new
    // one rather than waiting on an answer that has already come back.
    void done.then((saved) => {
      if (!saved && sending.current?.done === done) sending.current = null;
    });
    return done;
  }, [patch, savedBrief, typed]);

  const generate = useCallback(async () => {
    setBusy(true);
    setError(null);
    // One press, and no step of its own: the words on the screen go with it rather
    // than before it, and the operator is never asked to save the Brief first (R191).
    if (!(await saveBrief())) {
      setBusy(false);
      return;
    }
    // Anything else changed on the way here - Asset Type, Target, the device slot -
    // is read by the same planning, so the command waits for those edits too.
    await settled.current;
    try {
      await api.spread(experimentId, 'initial');
      navigate(`/experiments/${experimentId}/explore`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Набор не удалось запустить.');
      setBusy(false);
    }
  }, [experimentId, navigate, saveBrief]);

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (experiment === null) return <section className="page" aria-busy="true" />;

  const started = open.detail !== null && open.detail.batches.length > 0;
  /**
   * What the Inventory carried over, said in the words of the cell it came from.
   *
   * The operator has to see that the App, the Store Listing and the Target are already
   * chosen before the generation - and to be able to correct any of them here.
   */
  const carriedListing = carriedListingName(experiment.storeListingId, listings);
  const carriedTarget =
    experiment.target.kind === 'market'
      ? `${countryName(experiment.target.market)} (${experiment.target.market}) · ${languageName(experiment.target.locale)}`
      : 'Global';

  return (
    <section className="page brief">
      <div className="page-head">
        <div>
          <p className="eyebrow">Brief</p>
          <h1>Что этот креатив должен заставить людей почувствовать?</h1>
          <p className="note">
            Asset Type — единственный обязательный выбор. Остальное необязательно, если у этого Experiment
            есть от чего идти: фраза здесь или хотя бы один материал.
          </p>
        </div>
        <div className="brief-launch">
          <button className="primary" type="button" disabled={busy || (!started && listings.value === null)} onClick={() => void generate()}>
            {busy ? 'Запускаю…' : started ? 'Сгенерировать ещё один набор' : 'Сгенерировать набор'}
          </button>
          <p className="note">
            {!started && listings.value === null
              ? 'Жду Store Listing: первый набор фиксирует, для какого он.'
              : spreadSize === 1
                ? 'Один Candidate за запуск через подписку. Provider и размеры Play остаются автоматическими.'
                : 'Шесть готовых Candidate. Provider, модель и размеры Play остаются автоматическими.'}
          </p>
        </div>
      </div>

      {error === null ? null : <Failure message={error} />}

      <div className="brief-layout">
        <div className="brief-board">
          {fromInventory === null ? null : (
            <div className="carried">
              <p className="eyebrow">Перенесено из Inventory</p>
              <p className="note" aria-busy={carriedListing === null ? 'true' : undefined}>
                {`${carriedListing ?? 'Store Listing: читаю название…'} · Target ${carriedTarget}`}
              </p>
              <Link to={fromInventory} className="secondary-link">
                Вернуться в Inventory
              </Link>
            </div>
          )}

          <fieldset className="asset-choices" disabled={!experiment.attributionEditable}>
            <legend>Asset Type</legend>
            {AssetTypeSchema.options.map((option) => (
              <label key={option} className={option === experiment.assetType ? 'asset-choice active' : 'asset-choice'}>
                <input
                  type="radio"
                  name="assetType"
                  value={option}
                  checked={option === experiment.assetType}
                  onChange={() => void patch({ assetType: option })}
                />
                {assetTypeLabel(option)}
              </label>
            ))}
            {experiment.attributionEditable ? null : (
              <p className="note">
                Asset Type, Store Listing и Target зафиксированы для этой работы. Для другого
                оформления или рынка начните новый Experiment.
              </p>
            )}
          </fieldset>

          <StoreListingField
            loaded={listings}
            storeListingId={experiment.storeListingId}
            editable={experiment.attributionEditable}
            always={fromInventory !== null}
            onChange={(storeListingId) => void patch({ storeListingId })}
          />

          <TargetField
            target={experiment.target}
            editable={experiment.attributionEditable}
            onChange={(target) => void patch({ target })}
          />

          {experiment.assetType !== 'screenshotSet' ? null : (
            <DeviceSlotField
              experiment={experiment}
              onChange={(deviceSlot) => void patch({ deviceSlot })}
            />
          )}

          <label htmlFor="brief-text">Brief</label>
          <p className="note" id="brief-hint">
            Можно смешивать слова, файлы и ссылки. Здесь нет промпта для инженерии.
          </p>
          <textarea
            id="brief-text"
            aria-describedby="brief-hint"
            value={briefText}
            rows={5}
            onChange={(event) => setTyped(event.target.value)}
            onBlur={() => void saveBrief()}
          />

          <SourceCandidates experiment={experiment} />

          <Materials experiment={experiment} onChanged={open.refresh} />
        </div>

        <aside className="brief-side">
          <Hypothesis experiment={experiment} onChange={(hypothesis) => void patch({ hypothesis })} />

          <h2>Creative Learning</h2>
          {experiment.appliedLearnings.length === 0 ? null : (
            <ul className="learning-chips" aria-label="Creative Learning в работе">
              {experiment.appliedLearnings.map((learning) => (
                <li key={learning.learningId} className="chip">
                  <span>{learning.text}</span>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      void api.unuseLearning(experimentId, learning.learningId).then(open.refresh);
                    }}
                  >
                    {`Не развивать ${learning.text}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <BriefSuggestions
            experimentId={experimentId}
            applied={experiment.appliedLearnings}
            onUsed={open.refresh}
          />
        </aside>
      </div>
    </section>
  );
}

/**
 * Which Store Listing the Experiment is for. It only asks when there is a choice: an App
 * with nothing but its Main Store Listing has no field here at all - unless the operator
 * came here from the Inventory, where that Store Listing was chosen for them and has to
 * be visible and correctable.
 */
function StoreListingField({
  loaded,
  storeListingId,
  editable,
  always,
  onChange,
}: {
  loaded: Loaded<{ listings: readonly StoreListingResource[] } | null>;
  storeListingId: string | null;
  editable: boolean;
  /** Show the field even when the App has no Custom Store Listing to choose from. */
  always: boolean;
  onChange: (storeListingId: string | null) => void;
}): ReactNode {
  if (loaded.error !== null) {
    // Hiding the field here would make an Experiment on a Custom Store Listing pass for
    // one on the Main Store Listing.
    return (
      <fieldset className="target-field">
        <legend>Store Listing</legend>
        <Failure message={`Store Listing этого App не удалось прочитать. ${loaded.error}`} onRetry={loaded.reload} />
      </fieldset>
    );
  }
  const custom = (loaded.value?.listings ?? []).filter(
    (listing) => listing.kind === 'custom' && (listing.archivedAt === null || listing.id === storeListingId),
  );
  if (custom.length === 0 && !always) return null;

  return (
    <fieldset className="target-field" disabled={!editable}>
      <legend>Store Listing</legend>
      <label htmlFor="store-listing">В сравнении с</label>
      <select
        id="store-listing"
        value={storeListingId ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">Main Store Listing</option>
        {custom.map((listing) => (
          <option key={listing.id} value={listing.id}>
            {listingLabel(listing)}
          </option>
        ))}
      </select>
    </fieldset>
  );
}

/** A Store Listing as it is named: its own name, and its parameter when it differs. */
function listingLabel(listing: StoreListingResource): string {
  return listing.listingParam === null || listing.listingParam === listing.name
    ? listing.name
    : `${listing.name} · ${listing.listingParam}`;
}

/**
 * The Store Listing the Inventory carried, named the way the field names it; `null`
 * while its name is still on the way.
 *
 * An id is never a name the operator knows a Store Listing by, so it is not shown in its
 * place: a read that failed is said as one, and the field below offers to repeat it.
 */
function carriedListingName(
  storeListingId: string | null,
  loaded: Loaded<{ listings: readonly StoreListingResource[] } | null>,
): string | null {
  if (storeListingId === null) return 'Store Listing «Main Store Listing»';
  if (loaded.value === null) return loaded.error === null ? null : 'Store Listing не прочитан';
  const found = loaded.value.listings.find((listing) => listing.id === storeListingId);
  return found === undefined ? 'Store Listing не найден среди Store Listings этого App' : `Store Listing «${listingLabel(found)}»`;
}

/** The Target is corrected where it is read, not on a settings screen. */
function TargetField({
  target,
  editable,
  onChange,
}: {
  target: Target;
  editable: boolean;
  onChange: (target: Target) => void;
}): ReactNode {
  const market = target.kind === 'market' ? target.market : '';
  const locale = target.kind === 'market' ? target.locale : '';

  return (
    <fieldset className="target-field" disabled={!editable}>
      <legend>Target</legend>
      <label htmlFor="target-kind">Охват</label>
      <select
        id="target-kind"
        value={target.kind}
        onChange={(event) =>
          onChange(event.target.value === 'global' ? { kind: 'global' } : { kind: 'market', market: 'US', locale: 'en-US' })
        }
      >
        <option value="global">Global</option>
        <option value="market">Один рынок</option>
      </select>
      {target.kind === 'market' ? (
        <>
          <label htmlFor="target-market">Рынок</label>
          <input
            id="target-market"
            defaultValue={market}
            key={`market-${market}`}
            onBlur={(event) => {
              if (event.target.value.length >= 2 && event.target.value !== market) {
                onChange({ kind: 'market', market: event.target.value, locale });
              }
            }}
          />
          <label htmlFor="target-locale">Локаль</label>
          <input
            id="target-locale"
            defaultValue={locale}
            key={`locale-${locale}`}
            onBlur={(event) => {
              if (event.target.value.length >= 2 && event.target.value !== locale) {
                onChange({ kind: 'market', market, locale: event.target.value });
              }
            }}
          />
        </>
      ) : null}
    </fieldset>
  );
}

/**
 * The Google Play slot this screenshot set is validated against.
 *
 * It is stored with the Experiment and never guessed from the dimensions of an image,
 * which is why it is corrected here, where it is read, rather than inferred later. Phone
 * is the ordinary default; every other slot asks for something the phone slot does not.
 */
function DeviceSlotField({
  experiment,
  onChange,
}: {
  experiment: ExperimentResource;
  onChange: (slot: ExperimentResource['deviceSlot'] & string) => void;
}): ReactNode {
  return (
    <div className="device-slot-field">
      <label htmlFor="brief-device-slot">Слот устройства</label>
      <select
        id="brief-device-slot"
        value={experiment.deviceSlot ?? 'phone'}
        disabled={!experiment.attributionEditable}
        onChange={(event) => onChange(event.target.value as ExperimentResource['deviceSlot'] & string)}
      >
        {DeviceSlotSchema.options.map((option) => (
          <option key={option} value={option}>
            {deviceSlotLabel(option)}
          </option>
        ))}
      </select>
      <p className="note">
        Слот задаёт, по какому контракту Google Play проверяется этот набор. Он хранится, а не выводится из
        размеров изображения.
      </p>
    </div>
  );
}

/** Proposed wording the operator owns: editable in place, and removable outright. */
function Hypothesis({
  experiment,
  onChange,
}: {
  experiment: ExperimentResource;
  onChange: (hypothesis: string | null) => void;
}): ReactNode {
  return (
    <div className="hypothesis">
      <label htmlFor="hypothesis">Hypothesis</label>
      {experiment.hypothesisProposed ? <p className="note">Предложено Studio. Отредактируйте или уберите.</p> : null}
      <textarea
        id="hypothesis"
        rows={3}
        key={experiment.hypothesis ?? 'none'}
        defaultValue={experiment.hypothesis ?? ''}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== (experiment.hypothesis ?? '')) onChange(next === '' ? null : next);
        }}
      />
      {experiment.hypothesis === null ? null : (
        <button type="button" className="ghost" onClick={() => onChange(null)}>
          Убрать Hypothesis
        </button>
      )}
    </div>
  );
}

/**
 * R162. What `Repeat for...` carried in, said as what it is.
 *
 * A repeated Candidate is a creative reference in the destination and nothing else - not
 * a copy, not a parent, not a branch, and it brings no Outcome of its own. The server has
 * recorded it that way since task 14 and no screen read the field, so in the destination
 * the reference was invisible: the Brief looked as though it had been written from
 * nothing. The link goes to the Candidate where it actually lives, which is the whole
 * point of it being a reference rather than a copy.
 */
function SourceCandidates({ experiment }: { experiment: ExperimentResource }): ReactNode {
  const ids = experiment.sourceCandidateIds;
  const loaded = useAsync<readonly CandidateResource[]>(
    async () => Promise.all(ids.map(async (id) => api.readCandidate(id))),
    [ids.join(',')],
  );
  if (ids.length === 0) return null;

  return (
    <div className="source-references">
      <p className="eyebrow">Принесено как креативная ссылка</p>
      <p className="note">
        Повторённый Candidate приходит сюда как ссылка. Он не копируется в этот Experiment, не становится
        родителем чего-либо здесь, а его Outcome остаётся у Experiment, где его тестировали.
      </p>
      <ul className="library-choices" aria-label="Принесено как креативная ссылка">
        {ids.map((id) => {
          const candidate = loaded.value?.find((one) => one.id === id) ?? null;
          return (
            <li key={id}>
              {candidate === null || candidate.artifactId === null ? (
                <span className="frame frame-blank" aria-hidden="true" />
              ) : (
                <img
                  className="frame"
                  src={`${ARTIFACTS_PATH}/${candidate.artifactId}`}
                  alt={candidate.headline}
                  loading="lazy"
                />
              )}
              {candidate === null ? (
                <span className="note">Читаю исходный Candidate…</span>
              ) : (
                <Link to={`/experiments/${candidate.experimentId}/refine/${candidate.id}`} className="secondary-link">
                  {`Открыть ${candidate.headline} там, где он сделан`}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Mixed input: the same panel takes words, files, links and material this App already
 * remembers. All four end up as the same selection, because a Brief that made the
 * operator manage four kinds of attachment would be four forms wearing one name.
 */
function Materials({ experiment, onChanged }: { experiment: ExperimentResource; onChanged: () => void }): ReactNode {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const library = useAsync(async () => api.readLibrary(experiment.appId), [experiment.appId]);

  /**
   * A link is read in a Job, so what it turned out to be arrives after the press.
   *
   * Without this the operator learns that an address could not be retrieved only by
   * reloading the page they are writing a Brief on - which is the same as not being
   * told. The item is the whole signal: `ingest.reference` publishes what it settled.
   * Watching every Job in the Workspace finish instead was a way around a worker that
   * announced nothing, and it made an ordinary spread re-read this shelf once per slot.
   */
  const reloadLibrary = library.reload;
  useResourceEvents((event) => {
    if (event.resource === 'libraryItem') reloadLibrary();
  });

  const select = useCallback(
    async (materialIds: readonly string[]) => {
      await api.updateExperiment(experiment.id, { materialIds: [...materialIds] });
      onChanged();
    },
    [experiment.id, onChanged],
  );

  const add = useCallback(
    async (libraryItemId: string) => {
      if (experiment.materialIds.includes(libraryItemId)) return;
      await select([...experiment.materialIds, libraryItemId]);
    },
    [experiment.materialIds, select],
  );

  return (
    <div className="drop-zone">
      <p className="eyebrow">{`Материалы · выбрано ${experiment.materialIds.length}`}</p>
      <label htmlFor="brief-files">Добавить файлы</label>
      <input
        id="brief-files"
        type="file"
        multiple
        /**
         * Two files chosen in one press are two materials.
         *
         * Selecting each one as it landed sent the selection this render was holding
         * plus that file - so the second call replaced the first, and an operator who
         * chose two ended up with `Materials · 1 selected` and a `Use` button beside
         * the file they had just added. What arrived together is selected together.
         */
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          void (async () => {
            const added: string[] = [];
            for (const file of files) {
              try {
                const artifact = await api.uploadArtifact(file);
                const item = await api.registerLibraryItem(experiment.appId, artifact.id, file.name);
                if (!experiment.materialIds.includes(item.id) && !added.includes(item.id)) added.push(item.id);
              } catch {
                setNote('Часть этих файлов не удалось прочитать.');
              }
            }
            if (added.length > 0) await select([...experiment.materialIds, ...added]);
          })();
        }}
      />
      <label htmlFor="brief-url">Добавить ссылку</label>
      <input id="brief-url" value={url} onChange={(event) => setUrl(event.target.value)} />
      <button
        type="button"
        className="ghost"
        disabled={url.trim() === ''}
        onClick={() => {
          void api
            .ingestReference(experiment.appId, url.trim())
            .then((response) => add(response.item.id))
            .then(() => setUrl(''))
            .catch(() => setNote('Эту ссылку не удалось прочитать.'));
        }}
      >
        Добавить ссылку
      </button>
      {note === null ? null : (
        <p className="failure" role="alert">
          {note}
        </p>
      )}

      <p className="eyebrow">App Library</p>
      {library.value === null || library.value.items.length === 0 ? (
        <p className="note">
          В Library этого App пока пусто. Всё добавленное выше запоминается здесь и может снова
          выбираться любым следующим Brief.
        </p>
      ) : (
        <ul className="library-choices" aria-label="Материал App Library">
          {library.value.items.map((item) => {
            const name = materialName(item);
            const retrieval = retrievalNote(item);
            const chosen = experiment.materialIds.includes(item.id);
            return (
              <li key={item.id}>
                <span>{`${name} · ${item.role}`}</span>
                {/* R021: a link that could not be read is still selectable, and says why. */}
                {retrieval === null ? null : <p className="note">{retrieval}</p>}
                <button
                  type="button"
                  className="ghost"
                  onClick={() => {
                    void (chosen
                      ? select(experiment.materialIds.filter((id) => id !== item.id))
                      : add(item.id));
                  }}
                >
                  {chosen ? `Убрать ${name}` : `Взять ${name}`}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Entering an Experiment without naming a stage lands on the Brief. */
export const surfaces: readonly Surface[] = [
  {
    id: 'experiment-brief',
    path: '/experiments/:experimentId/brief',
    title: 'Brief',
    render: (params) => <Brief experimentId={params.experimentId ?? ''} />,
  },
  {
    id: 'experiment-entry',
    path: '/experiments/:experimentId',
    title: 'Brief',
    render: (params) => <Brief experimentId={params.experimentId ?? ''} />,
  },
];
